import React, { useMemo } from 'react';
import { getPreviousLocation } from 'helpers/navigationHistory';
import PropTypes from 'prop-types';

import { StorageManager, useLabel } from '@atlas/core';
import { Icon, Label, Tooltip } from '@atlas/ui';

import {
  CXFROM_STORAGE_KEY,
  OAUTH_FLOW_TYPE_STORAGE_KEY,
  OAUTH_FLOW_TYPES,
} from 'common/constants';
import { LocalConfig } from 'common/utils/utils';
import { useGoogleRegistrationPostHog } from 'hooks/usePostHog';

const SOCIAL_LINKS = {
  GOOGLE: ({ projectId }) => `/api/auth/oauth2/authorization/google-${projectId}`,
};

const resolvePageRefer = () => {
  const cxFromParam = new URLSearchParams(window.location.search).get('cxfrom');
  const cxFromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);

  return (
    cxFromParam ?? cxFromPath ?? getPreviousLocation()?.pathname ?? window.location.pathname ?? null
  );
};

const SocialProviderButton = ({ disabled, hint, link, name, onClick }) => {
  const content = (
    <>
      <Icon height={26} name="google" width={26} />
      <Label message={`social_login_${name.toLowerCase()}`} />
    </>
  );

  if (disabled) {
    return (
      <Tooltip message={hint} placement="top" tooltipClassName="social-signup-tooltip">
        <button className="social-signup-item" disabled type="button">
          {content}
        </button>
      </Tooltip>
    );
  }

  return (
    <a className="social-signup-item" href={link} onClick={onClick}>
      {content}
    </a>
  );
};

SocialProviderButton.propTypes = {
  disabled: PropTypes.bool,
  hint: PropTypes.string,
  link: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const SocialProviders = ({
  disabled = false,
  flowType,
  items = [],
  redirectPath,
  totalRegistrationSteps = 1,
}) => {
  const { handleGoogleClick } = useGoogleRegistrationPostHog();
  const inactiveButtonHint = useLabel('google_inactive_button_hint');

  const providers = useMemo(
    () =>
      items.map((provider, i) => ({
        id: i,
        link: SOCIAL_LINKS[provider]({ projectId: LocalConfig.get('projectId') }),
        name: provider,
      })),
    [items],
  );

  if (!providers.length) return null;

  const handleClick = () => {
    handleGoogleClick({ flowType, pageRefer: resolvePageRefer(), totalRegistrationSteps });

    if (redirectPath) {
      StorageManager.setItem(CXFROM_STORAGE_KEY, redirectPath);
    }
    if (flowType) {
      StorageManager.setItem(OAUTH_FLOW_TYPE_STORAGE_KEY, flowType);
    }
  };

  return (
    <div className="social-signup">
      <div className="social-signup-title">
        <hr />
        <Label message="social_login_with" />
        <hr />
      </div>

      {providers.map(({ id, link, name }) => (
        <SocialProviderButton
          disabled={disabled}
          hint={inactiveButtonHint}
          key={id}
          link={link}
          name={name}
          onClick={handleClick}
        />
      ))}
    </div>
  );
};

SocialProviders.propTypes = {
  disabled: PropTypes.bool,
  flowType: PropTypes.oneOf(Object.values(OAUTH_FLOW_TYPES)),
  items: PropTypes.arrayOf(PropTypes.string),
  redirectPath: PropTypes.string,
  totalRegistrationSteps: PropTypes.number,
};

export default SocialProviders;
